/* eslint-disable no-nested-ternary */
import {
  Label,
  Listbox,
  ListboxButton,
  ListboxOption,
  ListboxOptions,
} from '@headlessui/react';
import { CheckIcon, ChevronUpDownIcon } from '@heroicons/react/20/solid';
import { cva, VariantProps } from 'class-variance-authority';
import React from 'react';
import { twMerge } from 'tailwind-merge';

const $select = cva([], {
  variants: {
    variant: {
      primary: [],
      disabled: [],
      invalid: [
        'ring-1',
        'ring-red-300',
        'focus:ring-2',
        'focus:ring-red-600',
        'border-red-300',
        'text-red-900',
      ],
    },
    sizeVariant: {
      sm: ['text-xs/4'],
      md: ['text-base', 'sm:text-sm'],
    },
  },
  defaultVariants: {
    variant: 'primary',
  },
});
export interface SelectProps
  extends React.InputHTMLAttributes<HTMLInputElement>,
    VariantProps<typeof $select> {
  label?: string;
  subText?: string;
  orientation?: 'vertical' | 'horizontal' | 'compact';
  sizeVariant?: 'sm' | 'md';
  options: {
    label: string;
    value: string | number;
    disabled?: boolean;
    hint?: string;
  }[];
  selected: { label: string; value: string | number } | null;
  setSelected: (value: { label: string; value: string | number }) => void;
  grid?: boolean;
  isInvalid?: boolean;
  validationMessage?: string;
  optional?: boolean;
  required?: boolean;
  placeholder?: string;
  actionLabel?: string;
  onActionClick?: () => void;
}

export function Select({
  label,
  subText,
  orientation = 'vertical',
  sizeVariant = 'md',
  options,
  selected,
  setSelected,
  grid = false,
  isInvalid = false,
  validationMessage = '',
  optional = false,
  required = false,
  placeholder,
  actionLabel,
  onActionClick,
  name,
  ...props
}: SelectProps) {
  const isHorizontal = orientation === 'horizontal';
  const isCompact = orientation === 'compact';

  const longestLabel = options.reduce(
    (longest, option) =>
      option.label.length > longest.length ? option.label : longest,
    ''
  );

  const approxCharWidth = 0.7; // em, adjust as needed for your font
  const minWidthEm = Math.ceil(longestLabel.length * approxCharWidth) + 3; // +3 for icon/padding

  return (
    <div className='flex flex-col grow'>
      <Listbox value={selected} onChange={setSelected} by='value'>
        <div
          className={twMerge(
            $select({ sizeVariant, variant: 'primary' }),
            'grow',
            isCompact
              ? 'flex flex-row items-center gap-2'
              : isHorizontal && grid
              ? 'grid grid-cols-[1fr_auto] items-center gap-2 w-full'
              : isHorizontal
              ? 'flex flex-row items-center space-x-4'
              : 'flex-col'
          )}
          {...props}
        >
        {label && (
          <div
            className={twMerge(
              isCompact
                ? 'flex items-center'
                : isHorizontal
                ? 'flex flex-col shrink-0 min-w-[10rem]'
                : 'flex items-center'
            )}
          >
            <Label
              className={`${
                isHorizontal && !isCompact ? 'mr-2' : ''
              }
            block font-inter text-sm font-medium leading-5 text-gray-900`}
            >
              {label}
              {required && (
                <span aria-hidden='true' className='ml-0.5 text-red-500'>
                  *
                </span>
              )}
            </Label>
            {optional && (
              <span
                className={`text-sm leading-5 font-normal font-inter text-gray-400 ${
                  isHorizontal && !isCompact ? '' : 'ml-2'
                }`}
              >
                Optional
              </span>
            )}
          </div>
        )}
        <div className={twMerge('relative', orientation === 'vertical' && label ? 'mt-2' : '', isHorizontal && grid ? 'w-48' : '')}>
          <ListboxButton
            style={{ minWidth: `${minWidthEm}em` }}
            disabled={props.disabled}
            name={name}
            aria-required={required || undefined}
            aria-invalid={isInvalid || undefined}
            className={twMerge(
              `relative w-full cursor-default rounded-md bg-white py-1.5 pl-3 pr-10 text-left text-gray-950 text-base leading-6 font-normal font-inter shadow-sm ring-1 ring-inset ring-gray-200 focus:outline-none focus:ring-2 focus:ring-blue-200 sm:text-sm h-8`,
              isInvalid &&
                'ring-red-300 border-red-300 text-red-900 focus:ring-red-600'
            )}
          >
            <span
              className={`block truncate ${
                props.disabled ? 'text-gray-300' : !selected && placeholder ? 'text-gray-400' : ''
              }`}
            >
              {selected?.label || placeholder}
            </span>
            <span className='pointer-events-none absolute inset-y-0 right-0 flex items-center pr-2'>
              <ChevronUpDownIcon
                aria-hidden='true'
                className={twMerge(
                  'h-5 w-5',
                  isInvalid
                    ? 'text-red-500'
                    : props.disabled
                    ? 'text-gray-200'
                    : 'text-gray-400'
                )}
              />
            </span>
          </ListboxButton>

          <ListboxOptions
            anchor='bottom'
            modal={false}
            transition
            className='z-[1200] mt-1 max-h-60 w-[var(--button-width)] overflow-auto rounded-md bg-white py-1 text-base shadow-lg ring-1 ring-black ring-opacity-5 focus:outline-none data-[closed]:data-[leave]:opacity-0 data-[leave]:transition data-[leave]:duration-100 data-[leave]:ease-in sm:text-sm'
          >
            {options.map((option) => (
              <ListboxOption
                key={option.value}
                value={option}
                disabled={option.disabled}
                className='group relative cursor-default select-none py-2 pl-3 pr-9 text-gray-900 data-[focus]:bg-primary data-[focus]:text-white data-[disabled]:cursor-not-allowed data-[disabled]:text-gray-300 data-[disabled]:data-[focus]:bg-white'
              >
                <span className='block truncate group-data-[selected]:font-semibold font-normal font-inter'>
                  {option.label}
                </span>
                {option.hint && (
                  <span
                    title={option.hint}
                    className='block truncate text-xs text-gray-400 group-data-[focus]:text-white/80 group-data-[disabled]:text-gray-300'
                  >
                    {option.hint}
                  </span>
                )}

                <span className='absolute inset-y-0 right-0 flex items-center pr-4 text-primary group-data-[focus]:text-white [.group:not([data-selected])_&]:hidden'>
                  <CheckIcon aria-hidden='true' className='h-5 w-5' />
                </span>
              </ListboxOption>
            ))}
            {actionLabel && onActionClick && (
              <>
                {options.length > 0 && (
                  <div className='border-t border-gray-100' role='separator' />
                )}
                <div
                  className='relative cursor-pointer select-none py-2 pl-3 pr-9 text-sm font-medium text-primary hover:bg-blue-50 transition-colors'
                  onMouseDown={(e) => {
                    e.preventDefault();
                    e.stopPropagation();
                    onActionClick();
                  }}
                  role='button'
                  tabIndex={-1}
                >
                  {actionLabel}
                </div>
              </>
            )}
          </ListboxOptions>
        </div>
      </div>
      </Listbox>
      {isInvalid && validationMessage && (
        <p className='mt-1 text-sm text-red-600'>{validationMessage}</p>
      )}
      {subText && (
        <p className='mt-1 text-sm leading-5 text-gray-500 font-inter'>
          {subText}
        </p>
      )}
    </div>
  );
}
